import { useState } from 'preact/hooks';
import { createRecord, getSettings, getDb, nextBidNumber, putRecord, softDelete } from '../../core/db';
import { jobsSig, nav, refreshCore, toast } from '../state';
import { Empty, STATUS_LABELS } from '../bits';
import type { Job, JobStatus } from '../../core/types';

export function JobsView() {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<'' | JobStatus>('');
  const [showRemoved, setShowRemoved] = useState(false);
  const [removed, setRemoved] = useState<Job[]>([]);
  const [confirming, setConfirming] = useState<Job | null>(null);
  const [busy, setBusy] = useState(false);

  const jobs = jobsSig.value.filter((j) => {
    if (status && j.status !== status) return false;
    if (!q) return true;
    const hay = `${j.title} ${j.siteAddress} ${j.bidNumber} ${j.notes ?? ''}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  });

  async function newJob() {
    const s = await getSettings();
    const bidNumber = await nextBidNumber();
    const job = await createRecord('jobs', {
      title: `Job ${bidNumber}`, siteAddress: '', status: 'lead', bidNumber,
      fin: { markupPct: s.defaults.markupPct, taxPct: s.defaults.taxPct, depositPct: s.defaults.depositPct, adjustCents: 0 },
      parcel: null,
    });
    await refreshCore();
    toast(`Job ${bidNumber} created`);
    nav(`#/job/${job.id}`);
  }

  /**
   * Field report 2026-07-27: "there is no way to delete projects that populate".
   * True — the list could only make jobs, never get rid of one, so a few minutes
   * of testing left junk on screen with no way to clear it.
   *
   * Removal is a tombstone, not an erase, which is what every other record in
   * this app does (§3.2). The bid number is not reused, the takeoff and any
   * issued documents stay on disk, and a backup taken afterwards still contains
   * them. That matters: an issued proposal is a document a client has seen, and
   * a landscaper tidying a list should not be able to destroy one by accident.
   */
  async function loadRemoved() {
    const all = (await (await getDb()).getAll('jobs')) as Job[];
    setRemoved(all.filter((j) => j.deletedAt).sort((a, b) => (b.deletedAt ?? '').localeCompare(a.deletedAt ?? '')));
  }

  async function toggleRemoved() {
    const next = !showRemoved;
    setShowRemoved(next);
    if (next) await loadRemoved();
  }

  async function doRemove(job: Job) {
    setBusy(true);
    try {
      await softDelete('jobs', job.id);
      await refreshCore();
      if (showRemoved) await loadRemoved();
      setConfirming(null);
      toast(`${job.bidNumber} removed. Nothing was destroyed — “Show removed” brings it back.`);
    } finally { setBusy(false); }
  }

  async function restore(job: Job) {
    setBusy(true);
    try {
      // the envelope types a tombstone as string | null, so clearing it is null
      await putRecord('jobs', { ...job, deletedAt: null });
      await refreshCore();
      await loadRemoved();
      toast(`${job.bidNumber} is back on the list.`);
    } finally { setBusy(false); }
  }

  return (
    <div>
      <div class="pagehead">
        <h1>Jobs</h1>
        <div style="display:flex;gap:8px;flex-wrap:wrap;align-items:center;">
          <input placeholder="Search jobs, addresses, bid numbers" value={q}
            onInput={(e) => setQ((e.target as HTMLInputElement).value)} style="min-width:260px;" aria-label="Search jobs" />
          <select value={status} onChange={(e) => setStatus((e.target as HTMLSelectElement).value as '' | JobStatus)} aria-label="Filter by status">
            <option value="">All statuses</option>
            {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <label style="display:flex;align-items:center;gap:6px;font-weight:400;">
            <input type="checkbox" style="min-height:auto;" checked={showRemoved}
              onChange={() => void toggleRemoved()} /> show removed
          </label>
          <button class="btn btn-primary" onClick={() => void newJob()}>New job</button>
        </div>
      </div>

      {jobs.length === 0 ? (
        <Empty>
          {jobsSig.value.length === 0
            ? <span>No jobs yet. Click <b>New job</b> to start one, or import a backup from the Backup page.</span>
            : <span>Nothing matches that search. Clear the search box or the status filter to see all {jobsSig.value.length} jobs.</span>}
        </Empty>
      ) : (
        <table class="data jobstable">
          <thead><tr><th>Bid</th><th>Job</th><th>Site address</th><th>Status</th><th>Updated</th><th></th></tr></thead>
          <tbody>
            {jobs.map((j) => (
              <tr key={j.id}>
                <td class="mono small"><a href={`#/job/${j.id}`}>{j.bidNumber}</a></td>
                <td><a href={`#/job/${j.id}`} style="color:inherit;font-weight:600;">{j.title}</a></td>
                <td class="muted">{j.siteAddress || '—'}</td>
                <td><span class="chip">{STATUS_LABELS[j.status]}</span></td>
                <td class="muted small num">{j.updatedAt.slice(0, 10)}</td>
                <td>
                  <button class="btn btn-small btn-ghost" disabled={busy}
                    aria-label={`Remove ${j.bidNumber} ${j.title}`}
                    onClick={() => setConfirming(j)}>Remove</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showRemoved && (
        <section style="margin-top:24px;" aria-label="Removed jobs">
          <h2 style="font-size:17px;margin-bottom:8px;">Removed</h2>
          {removed.length === 0 ? (
            <Empty>Nothing has been removed.</Empty>
          ) : (
            <table class="data">
              <thead><tr><th>Bid</th><th>Job</th><th>Removed</th><th></th></tr></thead>
              <tbody>
                {removed.map((j) => (
                  <tr key={j.id} style="opacity:.7;">
                    <td class="mono small">{j.bidNumber}</td>
                    <td>{j.title}</td>
                    <td class="muted small num">{(j.deletedAt ?? '').slice(0, 10)}</td>
                    <td>
                      <button class="btn btn-small btn-ghost" disabled={busy}
                        aria-label={`Put ${j.bidNumber} back`}
                        onClick={() => void restore(j)}>Put back</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p class="small muted" style="margin-top:8px;">
            Removed jobs stay on this device and in every backup. Nothing here has been destroyed.
          </p>
        </section>
      )}

      {confirming && (
        <div class="modal-backdrop" role="dialog" aria-modal="true" aria-label="Remove job">
          <div class="card" style="max-width:460px;">
            <h2 style="margin-bottom:8px;">Remove {confirming.bidNumber}?</h2>
            <p style="margin-bottom:6px;">
              <b>{confirming.title}</b>{confirming.siteAddress ? ` — ${confirming.siteAddress}` : ''}
            </p>
            <p class="small muted" style="margin-bottom:14px;">
              It comes off the list. The takeoff, the inventory and any issued documents stay on this
              device and in your backups, and “show removed” puts it back. The bid number is not reused.
            </p>
            <div style="display:flex;gap:8px;justify-content:flex-end;">
              <button class="btn btn-ghost" disabled={busy} onClick={() => setConfirming(null)}>Keep it</button>
              <button class="btn btn-primary" disabled={busy} onClick={() => void doRemove(confirming!)}>
                {busy ? 'Removing…' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
